import React from 'react';
import { createStackNavigator } from '@react-navigation/stack';
import { useTranslation } from 'react-i18next';
import useTheme from '../../hooks/useTheme';
import { getStackScreenOptions } from '../config/screenOptions';
import { ProfileStackParamList } from '../types/navigation.types';
import { createLazyScreen } from '../utils/lazyScreen';
import { SCREEN_TITLES } from '../config/constants';

// Lazy load screens
const ProfileScreen = createLazyScreen(() => import('../../screens/profile/profile-screen'));
const ThemeSettingsScreen = createLazyScreen(() => import('../../screens/profile/theme-settings-screen'));
const AccountSettingsScreen = createLazyScreen(() => import('../../screens/profile/account-settings-screen'));
const TeamManagementScreen = createLazyScreen(() => import('../../screens/profile/team-management-screen'));
const NotificationSettingsScreen = createLazyScreen(() => import('../../screens/profile/notification-settings-screen'));
const PrivacySettingsScreen = createLazyScreen(() => import('../../screens/profile/privacy-settings-screen'));
const IntegrationsSettingsScreen = createLazyScreen(() => import('../../screens/profile/integrations-settings-screen'));
const HelpSupportScreen = createLazyScreen(() => import('../../screens/profile/help-support-screen'));
const HelpCategoryScreen = createLazyScreen(() => import('../../screens/help/help-category-screen'));
const HelpArticleDetailScreen = createLazyScreen(() => import('../../screens/help/help-article-detail-screen'));
const HelpFeedbackScreen = createLazyScreen(() => import('../../screens/help/help-feedback-screen'));
const HelpWorkflowScreen = createLazyScreen(() => import('../../screens/help/help-workflow-screen'));
const HelpAnalyticsScreen = createLazyScreen(() => import('../../screens/help/help-analytics-screen'));
const OrganizationSettingsScreen = createLazyScreen(() => import('../../screens/organization/organization-settings-screen'));
const OrganizationEditScreen = createLazyScreen(() => import('../../screens/organization/organization-edit-screen'));
const OrganizationCaptureScreen = createLazyScreen(() => import('../../screens/organization/organization-capture-screen'));
const OrganizationMembersScreen = createLazyScreen(() => import('../../screens/organization/organization-members-screen'));
const AddMemberScreen = createLazyScreen(() => import('../../screens/organization/add-member-screen'));
const UserManagementScreen = createLazyScreen(() => import('../../screens/admin/user-management-screen'));
const OpenSourceLicensesScreen = createLazyScreen(() => import('../../screens/profile/open-source-licenses-screen'));
const PrivacyPolicyScreen = createLazyScreen(() => import('../../screens/legal/privacy-policy-screen').then(m => ({ default: m.PrivacyPolicyScreen })));
const TermsOfServiceScreen = createLazyScreen(() => import('../../screens/legal/terms-of-service-screen').then(m => ({ default: m.TermsOfServiceScreen })));
const LinkedDevicesScreen = createLazyScreen(() => import('../../screens/profile/linked-devices-screen'));

const Stack = createStackNavigator<ProfileStackParamList>();

export function ProfileStack() {
  const { t } = useTranslation();
  const { colors } = useTheme();

  return (
    <Stack.Navigator screenOptions={getStackScreenOptions(colors)}>
      <Stack.Screen
        name="ProfileHome"
        component={ProfileScreen}
        options={{ title: t('profile.title') }}
      />
      <Stack.Screen
        name="ThemeSettings"
        component={ThemeSettingsScreen}
        options={{ title: t('profile.themeSettings') }}
      />
      <Stack.Screen
        name="AccountSettings"
        component={AccountSettingsScreen}
        options={{ title: t('profile.accountSettings') }}
      />
      <Stack.Screen
        name="TeamManagement"
        component={TeamManagementScreen}
        options={{ title: t(SCREEN_TITLES.TEAM_MANAGEMENT, 'Team Management') }}
      />
      <Stack.Screen
        name="NotificationSettings"
        component={NotificationSettingsScreen}
        options={{ title: t('profile.notificationSettings') }}
      />
      <Stack.Screen
        name="PrivacySettings"
        component={PrivacySettingsScreen}
        options={{ title: t('profile.privacySettings') }}
      />
      <Stack.Screen
        name="IntegrationsSettings"
        component={IntegrationsSettingsScreen}
        options={{ title: t('profile.integrationsSettings') }}
      />
      <Stack.Screen
        name="HelpSupport"
        component={HelpSupportScreen}
        options={{ title: t('profile.helpSupport') }}
      />
      <Stack.Screen
        name="HelpCategory"
        component={HelpCategoryScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="HelpArticleDetail"
        component={HelpArticleDetailScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="HelpFeedback"
        component={HelpFeedbackScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="HelpWorkflow"
        component={HelpWorkflowScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="HelpAnalytics"
        component={HelpAnalyticsScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="OrganizationSettings"
        component={OrganizationSettingsScreen}
        options={{ title: t('organization.settings') }}
      />
      <Stack.Screen
        name="OrganizationEdit"
        component={OrganizationEditScreen}
        options={{ title: t('organization.edit') }}
      />
      <Stack.Screen
        name="OrganizationCapture"
        component={OrganizationCaptureScreen}
        options={{ title: t('organization.capture') }}
      />
      <Stack.Screen
        name="OrganizationMembers"
        component={OrganizationMembersScreen}
        options={{ title: t('organization.members') }}
      />
      <Stack.Screen
        name="AddMember"
        component={AddMemberScreen}
        options={{ title: t(SCREEN_TITLES.ADD_MEMBER, 'Add New Member') }}
      />
      <Stack.Screen
        name="UserManagement"
        component={UserManagementScreen}
        options={{ title: t('admin.userManagement') }}
      />
      <Stack.Screen
        name="OpenSourceLicenses"
        component={OpenSourceLicensesScreen}
        options={{ title: t('helpSupport.openSource') }}
      />
      <Stack.Screen
        name="PrivacyPolicy"
        component={PrivacyPolicyScreen}
        options={{ title: t(SCREEN_TITLES.PRIVACY_POLICY, 'Privacy Policy') }}
      />
      <Stack.Screen
        name="TermsOfService"
        component={TermsOfServiceScreen}
        options={{ title: t(SCREEN_TITLES.TERMS_OF_SERVICE, 'Terms of Service') }}
      />
      <Stack.Screen
        name="LinkedDevices"
        component={LinkedDevicesScreen}
        options={{ title: t('linkedDevices.title', 'Linked Devices') }}
      />
    </Stack.Navigator>
  );
}